import { useAppSelector } from '@/store/hooks';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { selectReportingV2Filters } from '@/store/slices/reportingV2/reportingV2Slice';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  CONVERSIONS_LABEL_LONG,
  compactCurrency,
  compactNum,
  entityLabel,
  isConversionsPerPlatformScope,
} from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { useReportWidget } from '../hooks/useReportWidget';
import { type ReportingPermissions, useReportingPermissions } from '../hooks/useReportingPermissions';
import { COLORS, cardSx } from '../theme';
import BarChart from './BarChart';
import MetricToggle from './MetricToggle';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';

const KEY = 'topCampaignsMetric';
const PAGE_SIZE = 10;

const CONVERSIONS_OPTION = { key: 'conversions', label: CONVERSIONS_LABEL_LONG };

const formatValue = (metric: string, value: unknown): string => {
  if (metric === 'spend' || metric === 'client_spend') return compactCurrency(value);
  return compactNum(value);
};

/**
 * #4625 — Spend is offered only with "Show wholesale Spend and CPM", Client
 * Spend only with the Client Spend permission; Impressions always appears.
 * Default ranks by whichever money metric the user can actually see
 * (Client Spend when Spend is withheld), not always Impressions. Both
 * exported standalone so the decision is unit-testable without mounting the
 * card (see TopCampaignsCard.test.ts).
 *
 * flank#4745 — Client Spend also needs `hasClientSpendData`: Bonneville-style
 * selections have the permission but no sold CPMs loaded yet, so ranking by
 * it would draw an empty $0–$1 chart.
 */
export const defaultMetric = ({
  canViewWholesaleSpend,
  canViewClientSpend,
  hasClientSpendData,
}: ReportingPermissions): string =>
  canViewWholesaleSpend ? 'impressions' : canViewClientSpend && hasClientSpendData ? 'client_spend' : 'impressions';

export const buildMetricOptions = (
  permissions: ReportingPermissions,
  perPlatform: boolean,
): { key: string; label: string }[] => {
  const base = [
    { key: 'impressions', label: 'Impressions' },
    ...(permissions.canViewWholesaleSpend ? [{ key: 'spend', label: 'Spend' }] : []),
    // flank#4745 — hidden, not shown-disabled, when the current selection has
    // no client spend data at all (see ReportingPermissions.hasClientSpendData).
    ...(permissions.canViewClientSpend && permissions.hasClientSpendData
      ? [{ key: 'client_spend', label: 'Client Spend' }]
      : []),
  ];
  return perPlatform ? [...base, CONVERSIONS_OPTION] : base;
};

/**
 * useReportingPermissions optimistically assumes Client Spend is allowed
 * until restrictedFields finishes loading. A manual pick made during that
 * window can turn out to be restricted once the real permissions resolve —
 * the guard must only protect a selection that's still valid, never one the
 * real permission check has since ruled out (flank#4650 CodeRabbit finding).
 */
export const reconcileMetric = (
  metric: string,
  userSelected: boolean,
  permissions: ReportingPermissions,
  perPlatform: boolean,
): string => {
  const stillAllowed = buildMetricOptions(permissions, perPlatform).some(o => o.key === metric);
  if (userSelected && stillAllowed) return metric;
  return defaultMetric(permissions);
};

/**
 * client_spend/client_cpm are computed server-side AFTER the SQL query runs
 * (enrichWithClientSpend) — not real queryable columns, so the backend hard-
 * rejects sorting by them (assertNoClientSpendSort, ribeye-api#2400). Sorting
 * by a computed metric has to happen here instead, once the rows come back.
 */
export const isComputedMetric = (metric: string): boolean => metric === 'client_spend' || metric === 'client_cpm';

/** Descending client-side rank by `metric`, applied before the rows are paged. */
export const sortByMetricDesc = (rows: ReportRow[], metric: string): ReportRow[] =>
  [...rows].sort((a, b) => Number(b[metric] ?? 0) - Number(a[metric] ?? 0));

const TopCampaignsCard = () => {
  const permissions = useReportingPermissions();
  const [metric, setMetric] = useState(() => defaultMetric(permissions));
  const [page, setPage] = useState(0);
  const filters = useAppSelector(selectReportingV2Filters);
  // Tracks whether the viewer picked a metric themselves, so the permission
  // effect below only ever sets a DEFAULT — it must never clobber a manual
  // choice once restrictedFields finishes loading (see useReportingPermissions).
  const userSelectedMetric = useRef(false);

  // This card always breaks down by campaign, never by platform, so the only way a row is
  // one DSP's is a single-platform filter. Otherwise the bars are conversions summed across
  // platforms that share no pixel and don't agree on what counts (#4477 AC6). Top Publishers
  // — the card directly below, same shape — was gated in #4514; this one was missed, so the
  // two disagreed on the same screen under the same filters (#4573).
  const perPlatform = isConversionsPerPlatformScope(filters.dsps, ['campaign']);
  const metricOptions = buildMetricOptions(permissions, perPlatform);

  // Widening the filter while Conversions is selected would keep querying and charting it
  // behind a control that no longer offers it.
  useEffect(() => {
    if (!perPlatform && metric === 'conversions') {
      setMetric('impressions');
      setPage(0);
    }
  }, [perPlatform, metric]);

  // Re-applies the permission default once restrictedFields has actually
  // loaded (useReportingPermissions optimistically assumes Client Spend is
  // allowed until then). A manual selection survives only if it's still
  // permitted under the resolved permissions — see reconcileMetric.
  useEffect(() => {
    setMetric(prev => reconcileMetric(prev, userSelectedMetric.current, permissions, perPlatform));
  }, [permissions.canViewWholesaleSpend, permissions.canViewClientSpend, permissions.hasClientSpendData, perPlatform]);

  // client_spend/client_cpm can't be the server sort field (see isComputedMetric
  // above), so rank by impressions instead and widen the pool — a much higher
  // limit than the usual 100, since a client-spend/CPM leader could easily sit
  // outside the top 100 by impressions. Not an exact ranking: a campaign outside
  // the top 1000 by impressions (the server's max allowed limit) that would
  // otherwise lead by Client Spend is still missed, but in practice campaign
  // counts per filter are well under that.
  const computedMetric = isComputedMetric(metric);
  const {
    rows: fetchedRows,
    loading,
    error,
  } = useReportWidget(KEY, {
    dimensions: ['campaign', 'campaign_name'],
    metrics: [metric],
    sort: [{ field: computedMetric ? 'impressions' : metric, dir: 'desc' }],
    limit: computedMetric ? 1000 : 100,
  });
  const rows = computedMetric ? sortByMetricDesc(fetchedRows, metric) : fetchedRows;
  const onExportCsv = useExportWidgetCsv('topCampaignsMetric', rows);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const pageRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  // Fall back to the campaign id so an unnamed campaign is still a
  // distinguishable bar rather than a bare dash (and two unnamed ones don't
  // collapse into a single category).
  const labels = pageRows.map(row => entityLabel(row.campaign_name, row.campaign, 'campaign'));
  const values = pageRows.map(row => Number(row[metric] ?? 0));

  const handleMetricChange = (next: string) => {
    userSelectedMetric.current = true;
    setMetric(next);
    setPage(0);
  };

  return (
    <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          Top Campaigns
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MetricToggle options={metricOptions} value={metric} onChange={handleMetricChange} />
          <WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />
        </Box>
      </Box>

      {loading ? (
        <WidgetSkeleton height={280} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No data for the selected range yet.
        </Typography>
      ) : (
        <>
          <BarChart
            labels={labels}
            values={values}
            format={v => formatValue(metric, v)}
            // flank#4744 — a wrapped 2-line campaign name needs more row height than a
            // single-line tick did, or the 2nd line overlaps the next bar.
            height={Math.max(280, pageRows.length * 36)}
            horizontal
          />

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
          >
            <Typography variant='caption' sx={{ color: COLORS.muted }}>
              Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, rows.length)} of {rows.length}
            </Typography>
            <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size='small'
                disabled={safePage === 0}
                onClick={() => setPage(p => p - 1)}
                sx={{ minWidth: 0, p: 0.5, color: COLORS.muted }}
              >
                <ChevronLeftRounded />
              </Button>
              <Typography variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                {safePage + 1} / {totalPages}
              </Typography>
              <Button
                size='small'
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                sx={{ minWidth: 0, p: 0.5, color: COLORS.muted }}
              >
                <ChevronRightRounded />
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TopCampaignsCard;
